import { useDispatch } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { AddressV2 } from '@obeta/models/lib/models/ShoppingCart/AddressV2'
import {
  createUserAddressGraphQL,
  CreateUserAddressGraphQLResultAction,
  deleteUserAddressGraphQL,
  updateCartGraphQL,
  updateUserAddressGraphQL,
  UpdateUserAddressGraphQLResultAction,
  UserActionTypes,
} from '../../actions'
import { UserAddressV2 } from '@obeta/models/lib/models/Users/UserV2'
import { DeliveryAddressV2 } from '@obeta/models/lib/models/ShoppingCart/DeliveryAddressV2'
import { useShoppingCartById } from '../useShoppingCartById'
import { useUserDataV2 } from '../useUserDataV2'
import { useEntities } from '../useEntities'
import { useActionNotification } from '../useActionNotification'
import { ShoppingCartContextEnum, StoreV2 } from '@obeta/models/lib/models'
import {
  ShippingGroup,
  ShippingOptions,
  ShippingType,
} from '@obeta/models/lib/models/ShoppingCart/ShippingOptions'
import { useLoadShippingOptions } from '..//useLoadShippingOptions'

export const useAddressActions = () => {
  const { user } = useUserDataV2()
  const cart = useShoppingCartById(user?.settings?.defaultCartId ?? '')
  const defaultStoreId = user?.settings?.defaultStoreId
  const selectedStoreId = cart?.shippingData.storeId ?? defaultStoreId ?? ''
  const stores = useEntities<StoreV2>('storesv2')

  let store = stores.find((store) => store.id === selectedStoreId)
  if (!store) {
    store = stores.find((store) => store.id === defaultStoreId)
  }
  const userAddresses = useEntities<UserAddressV2>('useraddressesv2')
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState(
    cart?.shippingData.addressId
  )

  const dispatch = useDispatch()

  useEffect(() => {
    if (cart?.shippingData.addressId) {
      setSelectedDeliveryAddressId(cart?.shippingData.addressId)
    }
  }, [cart])

  const waitForCreateUserAddressResultAction = useActionNotification(
    UserActionTypes.CreateUserAddressGraphQLResult
  )
  const waitForUpdateUserAddressResultAction = useActionNotification(
    UserActionTypes.UpdateUserAddressGraphQLResult
  )
  const createUserAddress = useCallback(
    (address: AddressV2, customName: string) => {
      dispatch(createUserAddressGraphQL(address, customName))
      waitForCreateUserAddressResultAction((action: CreateUserAddressGraphQLResultAction) => {
        if (action.success && action.addressId) {
          setSelectedDeliveryAddressId(action.addressId)
        }
      })
    },
    [dispatch, waitForCreateUserAddressResultAction]
  )

  const deleteUserAddress = useCallback(
    (addressId: string) => {
      dispatch(deleteUserAddressGraphQL(addressId))
    },
    [dispatch]
  )

  const updateUserAddress = useCallback(
    (addressId: string, address: AddressV2, customName: string) => {
      dispatch(updateUserAddressGraphQL(addressId, address, customName))
      waitForUpdateUserAddressResultAction((action: UpdateUserAddressGraphQLResultAction) => {
        if (action.success) {
          setSelectedDeliveryAddressId(addressId)
        }
      })
    },
    [dispatch, waitForUpdateUserAddressResultAction]
  )

  const shippingOptionsForPickUp: ShippingOptions[] = useLoadShippingOptions(
    {
      cartId: cart?.id ?? '',
      shippingGroup: ShippingGroup.Pickup,
    },
    ShoppingCartContextEnum.CHECKOUT_PICKUP,
    selectedStoreId,
    ''
  )
  const shippingOptionsForDelivery: ShippingOptions[] = useLoadShippingOptions(
    {
      cartId: cart?.id ?? '',
      shippingGroup: ShippingGroup.Delivery,
    },
    ShoppingCartContextEnum.CHECKOUT_DELIVERY,
    '',
    cart?.shippingData.deliveryAddress
  )

  // save delivery address - Lieferung
  const updateDeliveryAddress = useCallback(
    (deliveryAddress: DeliveryAddressV2, addressId?: string | undefined) => {
      if (cart) {
        cart.shippingData.deliveryAddress = deliveryAddress
        const shippingDate =
          shippingOptionsForDelivery.find(
            (shippingOption) => shippingOption.isDefault && shippingOption.isEnabled
          )?.shippingDates[0].date ?? ''

        // Note that userAddresses all have addressIds, while addresses edited via ShoppingCartShippingForm don't
        if (addressId !== undefined) {
          cart.shippingData.addressId = addressId
          cart.shippingData.storeId = ''

          // keep shipping type if delivery was already selected with a shippingType set
          if (!['DefaultParcel', 'FastParcel', 'Cargo'].includes(cart.shippingData.shippingType)) {
            const defaultDelivery = shippingOptionsForDelivery.find(
              (shippingOption) => shippingOption.isDefault && shippingOption.isEnabled
            )
            cart.shippingData.shippingType = defaultDelivery
              ? defaultDelivery.shippingType
              : ShippingType.DefaultParcel
          }
          setSelectedDeliveryAddressId(addressId)
        }

        cart.shippingData.shippingDate = shippingDate
        dispatch(updateCartGraphQL(cart))
      }
    },
    [cart, dispatch, shippingOptionsForDelivery]
  )
  const onSelectUserAddressId = useCallback(
    (addressId: string) => {
      const selectedDeliveryAddress = userAddresses.find(
        (userAddress) => userAddress.addressId === addressId
      )
      if (selectedDeliveryAddress) {
        updateDeliveryAddress(selectedDeliveryAddress.address, selectedDeliveryAddress.addressId)
      }
    },
    [updateDeliveryAddress, userAddresses]
  )

  // save pickup address - Abholung
  const selectStoreId = (storeId: string) => {
    if (cart) {
      cart.shippingData.storeId = storeId

      // keep shipping type if pickup was already selected with a shippingType set
      if (
        !['DefaultPickup', 'ExpressPickup', 'PickupRenzbox'].includes(
          cart.shippingData.shippingType
        )
      ) {
        const defaultPickup = shippingOptionsForPickUp.find(
          (shippingOption) => shippingOption.isDefault && shippingOption.isEnabled
        )

        cart.shippingData.shippingType = defaultPickup
          ? defaultPickup.shippingType
          : ShippingType.DefaultPickup
      }
      cart.shippingData.shippingDate =
        shippingOptionsForPickUp.find(
          (shippingOption) => shippingOption.isDefault && shippingOption.isEnabled
        )?.shippingDates[0].date ?? ''

      dispatch(updateCartGraphQL(cart))
    }
  }
  return {
    createUserAddress,
    updateUserAddress,
    updateDeliveryAddress,
    deleteUserAddress,
    onSelectUserAddressId,
    selectedDeliveryAddressId,
    store,
    selectedStoreId,
    selectStoreId,
  }
}
